var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { deriveRowId } from "@hyper-hyper-space/hhs3_rdb";
import { DiagnosticBag, err, ok } from "../diagnostics.js";
import { compileMigrationRules } from "../compile/ddl.js";
import { buildAlterColumnsOf } from "../compile/rule_scope.js";
import { lowerSelectQuery } from "../compile/query.js";
import { asJsonLiteral, resolveCreator, resolveValue } from "./values.js";
/** Reserved INSERT / WITH ROWS pseudo-column; not a schema column. */
export const PSEUDO_COLUMN_UUID = 'uuid';
export function bind(statement, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const diagnostics = new DiagnosticBag();
        try {
            switch (statement.kind) {
                case 'create-database':
                    return ok(yield bindCreateDatabase(statement, context));
                case 'create-schema':
                    return ok(yield bindCreateSchema(statement, context));
                case 'create-tablegroup':
                    return ok(yield bindCreateTableGroup(statement, context));
                case 'add-member':
                    return ok(yield bindAddMember(statement, context));
                case 'alter-schema':
                    return ok(yield bindAlterSchema(statement, context));
                case 'update-schema':
                    return ok(yield bindUpdateSchema(statement, context));
                case 'update-ref':
                    return ok(yield bindUpdateRef(statement, context));
                case 'insert':
                    return ok(yield bindInsert(statement, context));
                case 'update':
                    return ok(yield bindUpdate(statement, context));
                case 'delete':
                    return ok(yield bindDelete(statement, context));
                case 'bundle':
                    return ok(yield bindBundle(statement, context));
                case 'set-view':
                    return ok(bindSetView(statement));
                case 'select':
                    return ok(yield bindSelect(statement, context));
                case 'log':
                    return ok(yield bindLog(statement, context));
            }
        }
        catch (e) {
            diagnostics.add('BIND_UNKNOWN_NAME', e instanceof Error ? e.message : String(e), statement.span);
            return err(diagnostics.all());
        }
    });
}
function bindCreateDatabase(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const creators = [];
        for (const expr of ast.creators) {
            creators.push(yield resolveCreator(expr, context));
        }
        return { kind: 'create-database', ast, seed: (_a = ast.seed) !== null && _a !== void 0 ? _a : context.createSeed('rdb', ast.name), creators };
    });
}
function bindCreateSchema(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const table of ast.tables) {
            for (const column of table.columns) {
                if (column.name === PSEUDO_COLUMN_UUID) {
                    throw new Error("column name 'uuid' is reserved");
                }
            }
        }
        const creators = [];
        for (const expr of ast.creators) {
            creators.push(yield resolveCreator(expr, context));
        }
        return { kind: 'create-schema', ast, creators };
    });
}
function bindCreateTableGroup(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const schema = yield context.resolveSchema(ast.schema);
        const schemaVersion = yield context.resolveVersion(ast.schemaVersion, { kind: 'schema', id: schema.id, schema: schema.schema });
        const bindings = {};
        for (const binding of ast.bindings) {
            bindings[binding.name] = (yield context.resolveGroup(binding.group)).id;
        }
        const initialRows = [];
        for (const row of ast.initialRows) {
            const { uuid, values } = yield bindInitialRowValues(row.values, context);
            initialRows.push({ table: row.table, uuid, values });
        }
        return {
            kind: 'create-tablegroup',
            ast,
            seed: (_a = ast.seed) !== null && _a !== void 0 ? _a : context.createSeed('group', ast.name),
            schema,
            schemaVersion,
            bindings,
            initialRows,
        };
    });
}
function bindAddMember(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const database = yield context.resolveDatabase(ast.database);
        const memberId = ast.member === 'schema'
            ? (yield context.resolveSchema(ast.target)).id
            : (yield context.resolveGroup(ast.target)).id;
        const at = yield context.resolveVersion(ast.at, { kind: 'object', id: database.id, object: database.db });
        if (database.db !== undefined && database.db.getCreators().length > 0 && ast.author === undefined) {
            throw new Error(`ADD ${ast.member === 'schema' ? 'SCHEMA' : 'TABLEGROUP'} requires BY when the database declares creators`);
        }
        const author = yield resolveEffectiveAuthor(ast.author, context);
        if (database.db !== undefined && database.db.getCreators().length > 0 && author === undefined) {
            throw new Error(`ADD ${ast.member === 'schema' ? 'SCHEMA' : 'TABLEGROUP'} requires an author when the database declares creators`);
        }
        const bound = { kind: 'add-member', ast, member: ast.member, database, memberId, at };
        if (ast.note !== undefined)
            bound.note = ast.note;
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
// Resolve the effective author of an authored statement: an explicit `BY`
// clause wins (`NOBODY` forces unauthored), otherwise the session's default
// author is used (which may itself be undefined / anonymous).
function resolveEffectiveAuthor(expr, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (expr === undefined)
            return context.currentAuthor();
        if (expr.kind === 'nobody')
            return undefined;
        if (expr.kind === 'variable')
            return context.resolveAuthor({ kind: 'variable', name: expr.name });
        return context.resolveAuthor({ kind: 'hash', prefix: expr.prefix });
    });
}
// A context view in which `$author` / `$me` resolve to the statement's effective
// author rather than the session default, so values like `VALUES ($author)`
// agree with the signer chosen by `BY`.
function contextWithAuthor(context, author) {
    return Object.assign(Object.assign({}, context), { resolveVariable: (name) => {
            if (name === 'me' || name === 'author') {
                if (author !== undefined)
                    return Promise.resolve(author);
                throw new Error(`$${name} has no value: the statement has no author (BY NOBODY or no default author)`);
            }
            return context.resolveVariable(name);
        } });
}
function bindInsert(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (ast.columns.length !== ast.values.length) {
            throw new Error(`INSERT column count (${ast.columns.length}) does not match value count (${ast.values.length})`);
        }
        const table = yield resolveTableRef(ast.table, context);
        const author = yield resolveEffectiveAuthor(ast.author, context);
        const valueContext = contextWithAuthor(context, author);
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: table.groupId, group: table.group });
        const { uuid, values } = yield bindInsertColumns(ast.columns, ast.values, context, table, at, valueContext);
        const bound = { kind: 'insert', ast, table, values, at, uuid };
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
function bindUpdate(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const table = yield resolveTableRef(ast.table, context);
        const author = yield resolveEffectiveAuthor(ast.author, context);
        const valueContext = contextWithAuthor(context, author);
        const values = {};
        for (const v of ast.values)
            values[v.column] = asJsonLiteral(yield resolveValue(v.value, valueContext));
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: table.groupId, group: table.group });
        const rowId = yield bindRowId(ast.rowId, context, table, at);
        const bound = { kind: 'update', ast, table, values, rowId, at };
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
function bindDelete(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const table = yield resolveTableRef(ast.table, context);
        const author = yield resolveEffectiveAuthor(ast.author, context);
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: table.groupId, group: table.group });
        const rowId = yield bindRowId(ast.rowId, context, table, at);
        const bound = { kind: 'delete', ast, table, rowId, at };
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
function bindBundle(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const group = yield context.resolveGroup(ast.group);
        if (group.group === undefined)
            throw new Error('BUNDLE target group is not loaded');
        const author = yield resolveEffectiveAuthor(ast.author, context);
        const valueContext = contextWithAuthor(context, author);
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: group.id, group: group.group });
        const writes = [];
        for (const write of ast.writes)
            writes.push(yield bindBundleWrite(write, valueContext, at, author === null || author === void 0 ? void 0 : author.keyId));
        const bound = { kind: 'bundle', ast, group, writes, at };
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
function bindBundleWrite(write, context, at, author) {
    return __awaiter(this, void 0, void 0, function* () {
        if (write.kind === 'insert') {
            if (write.columns.length !== write.values.length)
                throw new Error('BUNDLE INSERT column count does not match value count');
            const table = yield context.resolveTable(write.table);
            const { uuid, values } = yield bindInsertColumns(write.columns, write.values, context, table, at, context);
            const op = { action: 'insert', rowId: deriveRowId(uuid, author), uuid, values };
            return { table: write.table.table, op };
        }
        if (write.kind === 'update') {
            const table = yield context.resolveTable(write.table);
            const values = {};
            for (const v of write.values)
                values[v.column] = asJsonLiteral(yield resolveValue(v.value, context));
            return { table: write.table.table, op: { action: 'update', rowId: yield bindRowId(write.rowId, context, table, at), values } };
        }
        const table = yield context.resolveTable(write.table);
        return { table: write.table.table, op: { action: 'delete', rowId: yield bindRowId(write.rowId, context, table, at) } };
    });
}
function bindSetView(ast) {
    const bound = { kind: 'set-view', ast, at: ast.at };
    if (ast.from !== undefined)
        bound.from = ast.from;
    return bound;
}
function bindAlterSchema(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const schema = yield context.resolveSchema(ast.schema);
        if (schema.schema === undefined)
            throw new Error('ALTER SCHEMA target is not loaded');
        const author = yield resolveEffectiveAuthor(ast.author, context);
        if (author === undefined)
            throw new Error('ALTER SCHEMA requires an author identity');
        const at = yield context.resolveVersion(ast.at, { kind: 'schema', id: schema.id, schema: schema.schema });
        const view = yield schema.schema.getView(at, at);
        const columnsOf = buildAlterColumnsOf(view, ast.rules);
        return { kind: 'alter-schema', ast, schema, rules: compileMigrationRules(ast.rules, columnsOf), author, at };
    });
}
function bindUpdateSchema(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const group = yield context.resolveGroup(ast.group);
        if (group.group === undefined)
            throw new Error('UPDATE SCHEMA target group is not loaded');
        const schema = yield context.resolveSchema(ast.schema);
        const version = yield context.resolveVersion(ast.version, { kind: 'schema', id: schema.id, schema: schema.schema });
        const author = yield resolveEffectiveAuthor(ast.author, context);
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: group.id, group: group.group });
        const bound = { kind: 'update-schema', ast, group, version, at };
        if (author !== undefined)
            bound.author = author;
        return bound;
    });
}
function bindUpdateRef(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const group = yield context.resolveGroup(ast.group);
        if (group.group === undefined)
            throw new Error('UPDATE REF target group is not loaded');
        const ref = yield resolveBoundGroupRef(ast.ref, group, context);
        const refVersion = yield context.resolveVersion(ast.version, { kind: 'group', id: ref.foreign.id, group: ref.foreign.group });
        const at = yield context.resolveVersion(ast.at, { kind: 'group', id: group.id, group: group.group });
        const author = yield resolveEffectiveAuthor(ast.author, context);
        return { kind: 'update-ref', ast, group, ref: ref.observeRef, version: refVersion, author, at };
    });
}
function resolveBoundGroupRef(ref, group, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (group.group === undefined)
            throw new Error('UPDATE REF target group is not loaded');
        const bindings = group.group.getBindings();
        let groupId;
        let observeRef;
        if (ref.kind === 'name') {
            if (ref.parts.length !== 1)
                throw new Error('UPDATE REF expects a bound group name, not group.table');
            const bound = bindings[ref.text];
            if (bound === undefined)
                throw new Error(`'${ref.text}' is not a bound group of '${group.id}'`);
            groupId = bound;
            observeRef = ref.text;
        }
        else {
            const matches = [...new Set(Object.values(bindings))].filter((id) => id.startsWith(ref.prefix));
            if (matches.length === 0)
                throw new Error(`Unknown bound group hash prefix '#${ref.prefix}'`);
            if (matches.length > 1)
                throw new Error(`Ambiguous bound group hash prefix '#${ref.prefix}'`);
            groupId = matches[0];
            observeRef = groupId;
        }
        const foreign = yield context.resolveGroup({ kind: 'name', text: groupId, parts: [groupId], span: ref.span });
        return { observeRef, foreign };
    });
}
function bindSelect(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (ast.from !== undefined && ast.at === undefined) {
            throw new Error('SELECT FROM version requires AT');
        }
        const table = yield resolveTableRef(ast.table, context);
        const scope = { kind: 'group', id: table.groupId, group: table.group };
        const defaultView = ast.at === undefined ? yield ((_a = context.resolveDefaultView) === null || _a === void 0 ? void 0 : _a.call(context, scope)) : undefined;
        const at = ast.at === undefined && defaultView !== undefined
            ? defaultView.at
            : yield context.resolveVersion(ast.at, scope);
        const from = ast.at === undefined && (defaultView === null || defaultView === void 0 ? void 0 : defaultView.from) !== undefined
            ? defaultView.from
            : yield context.resolveVersion((_b = ast.from) !== null && _b !== void 0 ? _b : ast.at, scope);
        const query = yield lowerSelectQuery(ast, context);
        return { kind: 'select', ast, table, query, at, from };
    });
}
function resolveTableRef(ref, context) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (ref.group !== undefined)
            return context.resolveTable(ref);
        const group = yield ((_a = context.resolveDefaultGroup) === null || _a === void 0 ? void 0 : _a.call(context));
        if (group === undefined) {
            throw new Error(`Table '${ref.table}' requires a group qualifier; use group.table or set a current group`);
        }
        return context.resolveTable(Object.assign(Object.assign({}, ref), { group }));
    });
}
function versionScopeForLogTarget(target) {
    switch (target.kind) {
        case 'group':
            return { kind: 'group', id: target.id, group: target.object };
        case 'table':
            return { kind: 'table', groupId: target.groupId, tableName: target.tableName, table: target.object };
        case 'schema':
            return { kind: 'schema', id: target.id, schema: target.object };
        case 'database':
            return { kind: 'object', id: target.id, object: target.object };
    }
}
function bindLog(ast, context) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (ast.from !== undefined && ast.at === undefined) {
            throw new Error('LOG FROM version requires AT');
        }
        const target = yield context.resolveLogTarget(ast.target);
        const scope = versionScopeForLogTarget(target);
        const fromScope = target.kind === 'table'
            ? { kind: 'group', id: target.groupId, group: target.group }
            : scope;
        const defaultView = ast.at === undefined ? yield ((_a = context.resolveDefaultView) === null || _a === void 0 ? void 0 : _a.call(context, scope)) : undefined;
        const at = ast.at === undefined && defaultView !== undefined
            ? defaultView.at
            : yield context.resolveVersion(ast.at, scope);
        const from = ast.at === undefined && (defaultView === null || defaultView === void 0 ? void 0 : defaultView.from) !== undefined
            ? defaultView.from
            : yield context.resolveVersion((_b = ast.from) !== null && _b !== void 0 ? _b : ast.at, fromScope);
        return { kind: 'log', ast, target, at, from, explain: ast.explain === true };
    });
}
function bindInsertColumns(columns, valueExprs, context, table, at, valueContext) {
    return __awaiter(this, void 0, void 0, function* () {
        let uuid;
        const values = {};
        for (let i = 0; i < columns.length; i += 1) {
            const column = columns[i];
            const expr = valueExprs[i];
            if (column === PSEUDO_COLUMN_UUID) {
                const lit = asJsonLiteral(yield resolveValue(expr, valueContext));
                if (typeof lit !== 'string')
                    throw new Error('uuid pseudo-column requires a string value');
                uuid = lit;
                continue;
            }
            if (expr.kind === 'hash') {
                if (context.resolveFkRowId === undefined) {
                    throw new Error('FK #prefix resolution is not available in this host');
                }
                values[column] = yield context.resolveFkRowId(expr.prefix, table, column, at, at);
            }
            else {
                values[column] = asJsonLiteral(yield resolveValue(expr, valueContext));
            }
        }
        return { uuid: uuid !== null && uuid !== void 0 ? uuid : context.createUuid(), values };
    });
}
function bindInitialRowValues(pairs, context) {
    return __awaiter(this, void 0, void 0, function* () {
        let uuid;
        const values = {};
        for (const pair of pairs) {
            if (pair.column === PSEUDO_COLUMN_UUID) {
                const lit = asJsonLiteral(yield resolveValue(pair.value, context));
                if (typeof lit !== 'string')
                    throw new Error('uuid pseudo-column requires a string value');
                uuid = lit;
                continue;
            }
            values[pair.column] = asJsonLiteral(yield resolveValue(pair.value, context));
        }
        return { uuid: uuid !== null && uuid !== void 0 ? uuid : context.createUuid(), values };
    });
}
function bindRowId(expr, context, table, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (expr.kind === 'hash') {
            if (context.resolveRowId === undefined) {
                throw new Error('rowId #prefix resolution is not available in this host');
            }
            return context.resolveRowId(expr, table, at, at);
        }
        if (expr.kind === 'name') {
            return expr.text;
        }
        const value = yield resolveValue(expr, context);
        if (typeof value === 'string')
            return value;
        if (typeof value === 'object' && value !== null && value.kind === 'key-id') {
            return value.keyId;
        }
        throw new Error('rowId must resolve to a hash string');
    });
}
